/* Markdown seguro para a base de conhecimento.

   Não existe conversão para HTML aqui, e isso é a defesa: o texto vira uma ÁRVORE de blocos e
   trechos, e quem a desenha (`componentes/conhecimento/Markdown.tsx`) monta elementos do React —
   que escapam tudo. HTML cru digitado no artigo aparece como texto; nunca é interpretado.

   O que a árvore aceita é uma lista fechada (títulos, parágrafo, listas, citação, código, régua,
   negrito, itálico, código em linha e link). Todo o resto fica como texto literal. Link só passa
   com `https:`, `http:`, `mailto:` ou caminho do próprio portal — `javascript:`, `data:` e
   parentes viram só o rótulo, sem âncora. Imagem não é desenhada (o texto alternativo fica). */

export type Trecho =
  | { tipo: "texto"; valor: string }
  | { tipo: "negrito"; filhos: Trecho[] }
  | { tipo: "italico"; filhos: Trecho[] }
  | { tipo: "codigo"; valor: string }
  | { tipo: "link"; href: string; filhos: Trecho[] };

export type Bloco =
  | { tipo: "titulo"; nivel: 2 | 3 | 4; filhos: Trecho[] }
  | { tipo: "paragrafo"; filhos: Trecho[] }
  | { tipo: "lista"; ordenada: boolean; itens: Trecho[][] }
  | { tipo: "citacao"; filhos: Trecho[] }
  | { tipo: "codigo"; valor: string }
  | { tipo: "regua" };

const PROTOCOLOS_PERMITIDOS = new Set(["https:", "http:", "mailto:"]);

/**
 * O endereço normalizado se for seguro; `null` se não for.
 *
 * Passa pelo `URL` do navegador de propósito: ele resolve as manhas que uma expressão regular
 * perde (`java\tscript:`, espaços à frente, maiúsculas) e a comparação é com o protocolo que ELE
 * enxergou. O que sai é o `href` normalizado, não a string digitada.
 */
export function enderecoSeguro(bruto: string): string | null {
  const texto = bruto.trim();
  if (texto.startsWith("/") && !texto.startsWith("//") && !texto.startsWith("/\\")) {
    return texto;
  }
  try {
    const url = new URL(texto);
    return PROTOCOLOS_PERMITIDOS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

const IMAGEM = /^!\[([^\]]*)\]\(([^)\s]*)\)/;
const LINK = /^\[([^\]]+)\]\(([^)\s]+)\)/;

function fechamento(texto: string, marca: string, de: number): number {
  const fim = texto.indexOf(marca, de);
  return fim > de ? fim : -1;
}

export function trechosDe(texto: string): Trecho[] {
  const saida: Trecho[] = [];
  let corrente = "";
  const despejar = () => {
    if (corrente) saida.push({ tipo: "texto", valor: corrente });
    corrente = "";
  };

  let i = 0;
  while (i < texto.length) {
    const resto = texto.slice(i);
    const letra = texto[i];

    if (letra === "`") {
      const fim = fechamento(texto, "`", i + 1);
      if (fim > 0) {
        despejar();
        saida.push({ tipo: "codigo", valor: texto.slice(i + 1, fim) });
        i = fim + 1;
        continue;
      }
    }
    if (resto.startsWith("**")) {
      const fim = fechamento(texto, "**", i + 2);
      if (fim > 0) {
        despejar();
        saida.push({ tipo: "negrito", filhos: trechosDe(texto.slice(i + 2, fim)) });
        i = fim + 2;
        continue;
      }
    }
    if (letra === "*" && !resto.startsWith("**")) {
      const fim = fechamento(texto, "*", i + 1);
      if (fim > 0 && texto[fim + 1] !== "*") {
        despejar();
        saida.push({ tipo: "italico", filhos: trechosDe(texto.slice(i + 1, fim)) });
        i = fim + 1;
        continue;
      }
    }
    if (letra === "!") {
      const imagem = IMAGEM.exec(resto);
      if (imagem) {
        corrente += imagem[1] ?? "";
        i += imagem[0].length;
        continue;
      }
    }
    if (letra === "[") {
      const link = LINK.exec(resto);
      if (link) {
        const filhos = trechosDe(link[1] ?? "");
        const href = enderecoSeguro(link[2] ?? "");
        despejar();
        if (href) saida.push({ tipo: "link", href, filhos });
        else saida.push(...filhos);
        i += link[0].length;
        continue;
      }
    }
    corrente += letra;
    i += 1;
  }
  despejar();
  return saida;
}

const TITULO = /^(#{1,4})\s+(.+?)\s*#*\s*$/;
const ITEM_NAO_ORDENADO = /^\s*[-*+]\s+(.*)$/;
const ITEM_ORDENADO = /^\s*\d+[.)]\s+(.*)$/;
const CITACAO = /^>\s?(.*)$/;
const REGUA = /^\s*([-*_])(\s*\1){2,}\s*$/;
const CERCA = /^\s*```/;

export function blocosDe(markdown: string): Bloco[] {
  const linhas = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocos: Bloco[] = [];
  let i = 0;

  while (i < linhas.length) {
    const linha = linhas[i] ?? "";
    if (!linha.trim()) {
      i += 1;
      continue;
    }
    if (CERCA.test(linha)) {
      const codigo: string[] = [];
      i += 1;
      while (i < linhas.length && !CERCA.test(linhas[i] ?? "")) {
        codigo.push(linhas[i] ?? "");
        i += 1;
      }
      i += 1;
      blocos.push({ tipo: "codigo", valor: codigo.join("\n") });
      continue;
    }
    const titulo = TITULO.exec(linha);
    if (titulo) {
      /* `#` é o título da página, que já é o título do artigo: dentro do texto começa em h2. */
      const nivel = Math.min(Math.max((titulo[1] ?? "#").length + 1, 2), 4) as 2 | 3 | 4;
      blocos.push({ tipo: "titulo", nivel, filhos: trechosDe(titulo[2] ?? "") });
      i += 1;
      continue;
    }
    if (REGUA.test(linha)) {
      blocos.push({ tipo: "regua" });
      i += 1;
      continue;
    }
    const naoOrdenado = ITEM_NAO_ORDENADO.test(linha);
    if (naoOrdenado || ITEM_ORDENADO.test(linha)) {
      const padrao = naoOrdenado ? ITEM_NAO_ORDENADO : ITEM_ORDENADO;
      const itens: Trecho[][] = [];
      while (i < linhas.length && padrao.test(linhas[i] ?? "")) {
        itens.push(trechosDe((padrao.exec(linhas[i] ?? "")?.[1] ?? "").trim()));
        i += 1;
      }
      blocos.push({ tipo: "lista", ordenada: !naoOrdenado, itens });
      continue;
    }
    if (CITACAO.test(linha)) {
      const partes: string[] = [];
      while (i < linhas.length && CITACAO.test(linhas[i] ?? "")) {
        partes.push((CITACAO.exec(linhas[i] ?? "")?.[1] ?? "").trim());
        i += 1;
      }
      blocos.push({ tipo: "citacao", filhos: trechosDe(partes.join(" ")) });
      continue;
    }
    /* Toda linha que o laço abaixo recusa (cerca, título, item, citação) já foi tratada acima:
       a primeira linha do parágrafo é sempre aceita e o laço avança. */
    const paragrafo: string[] = [];
    while (i < linhas.length) {
      const atual = linhas[i] ?? "";
      if (
        !atual.trim() ||
        CERCA.test(atual) ||
        TITULO.test(atual) ||
        ITEM_NAO_ORDENADO.test(atual) ||
        ITEM_ORDENADO.test(atual) ||
        CITACAO.test(atual)
      ) {
        break;
      }
      paragrafo.push(atual.trim());
      i += 1;
    }
    blocos.push({ tipo: "paragrafo", filhos: trechosDe(paragrafo.join(" ")) });
  }
  return blocos;
}
